/* Phase 5 · Gate Pass Register (search + filters + manual pass creator) */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Printer } from "lucide-react";

const AUTH_ROLES = new Set(["supervisor", "coordinator", "director", "prod_qc_coordinator", "stores_coordinator"]);

const badge = { Expected: "bg-slate-100 text-slate-700", Passed: "bg-blue-100 text-blue-800", Returned: "bg-emerald-100 text-emerald-800", Mismatch: "bg-red-100 text-red-800" };

export default function GatePassRegister() {
  const { user } = useAuth();
  const canAuthorise = AUTH_ROLES.has(user?.role);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ status: "", type: "" });
  const [showManual, setShowManual] = useState(false);
  const [manual, setManual] = useState({ type: "Inward", returnable: false, item_description: "", vehicle_no: "", person_name: "" });

  const load = () => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.type) params.type = filters.type;
    api.get("/gate-passes", { params }).then(r => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const createManual = async () => {
    if (!manual.item_description.trim()) return toast.error("Description required");
    try {
      await api.post("/gate-passes/manual", manual);
      toast.success("Manual pass created");
      setShowManual(false);
      setManual({ type: "Inward", returnable: false, item_description: "", vehicle_no: "", person_name: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 md:p-6" data-testid="gp-register">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#7C8E9E]">SECURITY</div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0B2340]">Gate Pass Register</h1>
        </div>
        {canAuthorise && (
          <button onClick={() => setShowManual(true)} data-testid="gp-manual-btn"
            className="px-4 py-2 rounded-full bg-[#B08B49] text-white text-sm">
            + Manual pass
          </button>
        )}
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {["", "Expected", "Passed", "Returned", "Mismatch"].map(s => (
          <button key={s} data-testid={`gp-filter-${s || "all"}`} onClick={() => setFilters({ ...filters, status: s })}
            className={`px-3 py-1 rounded-full text-xs ${filters.status === s ? "bg-[#0B2340] text-white" : "bg-white border border-slate-200"}`}>
            {s || "All"}
          </button>
        ))}
        <span className="mx-2 text-xs text-[#7C8E9E]">·</span>
        {["", "Inward", "Outward"].map(t => (
          <button key={t} data-testid={`gp-type-${t || "all"}`} onClick={() => setFilters({ ...filters, type: t })}
            className={`px-3 py-1 rounded-full text-xs ${filters.type === t ? "bg-[#0B2340] text-white" : "bg-white border border-slate-200"}`}>
            {t || "Any"}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-slate-100 overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="gp-table">
          <thead className="text-[11px] uppercase tracking-wider text-[#7C8E9E]">
            <tr className="border-b border-slate-100">
              <th className="text-left p-3">Pass No</th>
              <th className="text-left p-3">Created</th>
              <th className="text-left p-3">Type</th>
              <th className="text-left p-3">Ref</th>
              <th className="text-left p-3">Description</th>
              <th className="text-left p-3">Vehicle</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Passed at</th>
              <th className="text-left p-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-slate-50" data-testid={`gp-row-${r.pass_no}`}>
                <td className="p-3 font-mono">{r.pass_no}</td>
                <td className="p-3 text-xs">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                <td className="p-3 text-xs">
                  {r.type} {r.returnable ? " · R" : ""}
                </td>
                <td className="p-3 text-xs uppercase text-[#7C8E9E]">{r.ref_type}</td>
                <td className="p-3">{r.item_description}</td>
                <td className="p-3 text-xs">{r.vehicle_no || "—"}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${badge[r.status]}`}>{r.status}</span>
                </td>
                <td className="p-3 text-xs">{r.passed_at?.slice(11, 16) || ""}</td>
                <td className="p-3">
                  {(r.status === "Passed" || r.status === "Returned") && (
                    <a href={`/gate/receipt/${r.id}`} target="_blank" rel="noopener"
                      data-testid={`gp-print-${r.pass_no}`}
                      className="text-[10px] text-[#4A627A] underline inline-flex items-center gap-1">
                      <Printer size={10} /> Receipt
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showManual && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-4 max-w-md w-full" data-testid="gp-manual-modal">
            <h3 className="font-bold mb-3">Manual Gate Pass</h3>
            <label className="text-xs block mb-2">
              <span className="text-[#4A627A]">Type</span>
              <select className="w-full border rounded px-2 py-1.5 mt-1"
                value={manual.type} onChange={e => setManual({ ...manual, type: e.target.value })}>
                <option>Inward</option>
                <option>Outward</option>
              </select>
            </label>
            <label className="text-xs block mb-2">
              <input type="checkbox" className="mr-2" checked={manual.returnable}
                data-testid="gp-manual-returnable"
                onChange={e => setManual({ ...manual, returnable: e.target.checked })} />
              Returnable
            </label>
            <label className="text-xs block mb-2">
              <span className="text-[#4A627A]">Item description</span>
              <input className="w-full border rounded px-2 py-1.5 mt-1"
                data-testid="gp-manual-desc"
                value={manual.item_description} onChange={e => setManual({ ...manual, item_description: e.target.value })} />
            </label>
            <label className="text-xs block mb-2">
              <span className="text-[#4A627A]">Vehicle no.</span>
              <input className="w-full border rounded px-2 py-1.5 mt-1"
                value={manual.vehicle_no} onChange={e => setManual({ ...manual, vehicle_no: e.target.value })} />
            </label>
            <label className="text-xs block mb-3">
              <span className="text-[#4A627A]">Person / carrier</span>
              <input className="w-full border rounded px-2 py-1.5 mt-1"
                value={manual.person_name} onChange={e => setManual({ ...manual, person_name: e.target.value })} />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowManual(false)} className="px-3 py-1 text-xs">Cancel</button>
              <button onClick={createManual} data-testid="gp-manual-submit"
                className="px-3 py-1 rounded-full bg-[#B08B49] text-white text-xs">Create pass</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
